/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdOutlineCancel, MdOutlineContentCopy } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdRemoveCircleOutline } from "react-icons/md";
import RequestWhitelistAddressCancelConfirmModal from "../../shared/RequestWhitelistAddressCancelConfirmModal";
import RequestWhitelistAddressApproveConfirmModal from "../../shared/RequestWhitelistAddressApproveConfirmModal";
import RequestWhitelistAddressRejectConfirmModal from "../../shared/RequestWhitelistAddressRejectConfirmModal";
import RequestWhitelistAddressBlacklistConfirmModal from "../../shared/RequestWhitelistAddressBlacklistConfirmModal";
import { ProfileContext } from "../../../../store/ProfileProvider";
import SweetAlert2 from "react-sweetalert2";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  wlad_id: string;
  status: string;
  address: string;
  asset: string;
  created_at: string;
  reason?: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

const WhitelistAddressList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [swalProps, setSwalProps] = useState<any>({});

  const [whitelistAddressToCancel, setWhitelistAddressToCancel] = useState<any>(null);
  const [isOpenWhitelistAddressCancelConfirmModal, setIsOpenWhitelistAddressCancelConfirmModal] = useState<boolean>(false);

  const [whitelistAddressToApprove, setWhitelistAddressToApprove] = useState<any>(null);
  const [isOpenWhitelistAddressApproveConfirmModal, setIsOpenWhitelistAddressApproveConfirmModal] = useState<boolean>(false);

  const [whitelistAddressToReject, setWhitelistAddressToReject] = useState<any>(null);
  const [isOpenWhitelistAddressRejectConfirmModal, setIsOpenWhitelistAddressRejectConfirmModal] = useState<boolean>(false);

  const [whitelistAddressToBlacklist, setWhitelistAddressToBlacklist] = useState<any>(null);
  const [isOpenWhitelistAddressBlacklistConfirmModal, setIsOpenWhitelistAddressBlacklistConfirmModal] = useState<boolean>(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const subaccountType = localStorage.getItem("subaccountType");

  interface WhitelistAddressStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: WhitelistAddressStatusItems[] = [
    { status: "pending", value: "pending", display: <Badge className={`${statusClasses} status-pending`}>Pending</Badge> },
    { status: "approved", value: "approved", display: <Badge className={`${statusClasses} status-approved`}>Approved</Badge> },
    { status: "rejected", value: "rejected", display: <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge> },
    { status: "cancelled", value: "cancelled", display: <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge> },
    { status: "blacklist", value: "blacklist", display: <Badge className={`${statusClasses} status-blacklist`}>Blacklist</Badge> },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  // Cancel handlers
  const handleClickCancel = (whitelistAddress: any) => {
    setWhitelistAddressToCancel(whitelistAddress);
    setIsOpenWhitelistAddressCancelConfirmModal(true);
  };
  const closeWhitelistAddressCancelConfirmModal = () => {
    setWhitelistAddressToCancel(null);
    setIsOpenWhitelistAddressCancelConfirmModal(false);
  };

  // Approve handlers
  const handleClickApprove = (whitelistAddress: any) => {
    setWhitelistAddressToApprove(whitelistAddress);
    setIsOpenWhitelistAddressApproveConfirmModal(true);
  };
  const closeWhitelistAddressApproveConfirmModal = () => {
    setWhitelistAddressToApprove(null);
    setIsOpenWhitelistAddressApproveConfirmModal(false);
  };

  // Reject handlers
  const handleClickReject = (whitelistAddress: any) => {
    setWhitelistAddressToReject(whitelistAddress);
    setIsOpenWhitelistAddressRejectConfirmModal(true);
  };
  const closeWhitelistAddressRejectConfirmModal = () => {
    setWhitelistAddressToReject(null);
    setIsOpenWhitelistAddressRejectConfirmModal(false);
  };

  // Blacklist handlers
  const handleClickBlacklist = (whitelistAddress: any) => {
    setWhitelistAddressToBlacklist(whitelistAddress);
    setIsOpenWhitelistAddressBlacklistConfirmModal(true);
  };
  const closeWhitelistAddressBlacklistConfirmModal = () => {
    setWhitelistAddressToBlacklist(null);
    setIsOpenWhitelistAddressBlacklistConfirmModal(false);
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.wlad_id}
          onClick={() => handleClickCopy(params.data.wlad_id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    if (
      userRole === "superadmin" &&
      (status === "rejected" || status === "blacklist") &&
      params.data.reason
    ) {
      return (
        <Tooltip title={params.data.reason} arrow placement="top">
          <div className="cursor-help">
            {statusItem ? statusItem.display : null}
          </div>
        </Tooltip>
      );
    }
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const { status } = params.data || {};
    const canCancel =
      userRole === "subaccount" &&
      userPermissions?.cancelWhitelistAddressRequest === true &&
      status === "pending";
    const canApprove =
      userRole === "superadmin" &&
      userPermissions?.approveWhitelistAddress === true &&
      status === "pending";
    const canReject =
      userRole === "superadmin" &&
      userPermissions?.rejectWhitelistAddress === true &&
      status === "pending";
    const canBlacklist =
      userRole === "superadmin" &&
      userPermissions?.blacklistWhitelistAddress === true &&
      status === "approved";

    return (
      <div className="flex items-center justify-end">
        {canCancel && (
          <Tooltip title="Mark as Cancel" arrow placement="top">
            <div>
              <IconButton
                id={`button-cancel-${params.data?.wlad_id}`}
                onClick={() => handleClickCancel(params.data)}
                size="small"
              >
                <MdOutlineCancel className="status-cancelled h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canApprove && (
          <Tooltip title="Mark as Approve" arrow placement="top">
            <div>
              <IconButton
                id={`button-approve-${params.data?.wlad_id}`}
                onClick={() => handleClickApprove(params.data)}
                size="small"
              >
                <AiOutlineCheckCircle className="status-approved h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canReject && (
          <Tooltip title="Mark as Reject" arrow placement="top">
            <div>
              <IconButton
                id={`button-reject-${params.data?.wlad_id}`}
                onClick={() => handleClickReject(params.data)}
                size="small"
              >
                <MdOutlineCancel className="status-rejected h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canBlacklist && (
          <Tooltip title="Mark as Blacklist" arrow placement="top">
            <div>
              <IconButton
                id={`button-blacklist-${params.data?.wlad_id}`}
                onClick={() => handleClickBlacklist(params.data)}
                size="small"
              >
                <MdRemoveCircleOutline className="status-rejected h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "nickname",
      headerName: "Nickname",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 70,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "network",
      headerName: "Network",
      sortable: false,
      width: 100,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "address",
      headerName: "Address",
      sortable: false,
      minWidth: 200,
      flex: 2,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 120,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 100,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const filterColumns = useCallback(() => {
    let rebuildColumns = [...columns];
    if (userRole !== "superadmin") {
      const excludeFields: string[] = ["subaccount"];
      if (!userPermissions?.cancelWhitelistAddressRequest) {
        excludeFields.push("actions");
      }
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    }
    return rebuildColumns;
  }, [userRole, userPermissions, subaccountType]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <>
      {/* Cancel Modal */}
      {userRole === "subaccount" && whitelistAddressToCancel && (
        <RequestWhitelistAddressCancelConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressCancelConfirmModal}
          dataToCancel={whitelistAddressToCancel}
          onClose={() => closeWhitelistAddressCancelConfirmModal()}
        />
      )}

      {/* Approve Modal */}
      {userRole === "superadmin" && whitelistAddressToApprove && (
        <RequestWhitelistAddressApproveConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressApproveConfirmModal}
          dataToApprove={whitelistAddressToApprove}
          onClose={() => closeWhitelistAddressApproveConfirmModal()}
        />
      )}

      {/* Reject Modal */}
      {userRole === "superadmin" && whitelistAddressToReject && (
        <RequestWhitelistAddressRejectConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressRejectConfirmModal}
          dataToReject={whitelistAddressToReject}
          onClose={() => closeWhitelistAddressRejectConfirmModal()}
        />
      )}

      {/* Blacklist Modal */}
      {userRole === "superadmin" && whitelistAddressToBlacklist && (
        <RequestWhitelistAddressBlacklistConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressBlacklistConfirmModal}
          dataToBlacklist={whitelistAddressToBlacklist}
          onClose={() => closeWhitelistAddressBlacklistConfirmModal()}
        />
      )}

      <Box className="card w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={filterColumns()}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.wlad_id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default WhitelistAddressList;
